/**
 * REST floors endpoints — docs/CONTRACTS.md §REST-API.
 *
 *   GET /devices/{id}/floors   — list floors on a lift device
 *   PUT /devices/{id}/floors   — replace the lift's floor list (full replacement)
 *
 * Floors are promoted out of device_config.settings.floors JSONB at Phase 2.
 * PUT requires the caller to be the device owner (Main User) or to hold the
 * explicit floors.configure permission grant.
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
// ── Shared floor schema ────────────────────────────────────────────────────
/** Floor grouping shown as sections in the lift setup screen. `ordinal`
 *  remains the single global sort key; category is a label on top of it. */
export const FloorCategorySchema = z.enum([
    'basement',
    'parking',
    'ground',
    'floor',
    'terrace',
]);
export const FloorSchema = z.object({
    id: UuidSchema,
    deviceId: UuidSchema,
    /**
     * Stable short identifier minted by the SERVER at save: the bare prefix for
     * the one basement / parking / ground / terrace a building has ("B", "P",
     * "G", "T"), numbered floors "F" + counter ("F3"). Unique per device,
     * stable across renames — the key used by call_floor and every lift status
     * frame. Clients never send it; the server matches/mints on replace.
     */
    floorId: z.string().min(1).max(16),
    building: z.string().min(1).max(128).nullable(),
    /** User-defined display name, e.g. "Lobby", "Sales Office". Free text —
     *  rides to the device as apply_config's `name` but is display-only there. */
    label: z.string().min(1).max(64),
    /** SIGNED physical position: ground = 0, below ground negative (basement,
     *  parking), above ground positive. NOT a relay index. */
    ordinal: z.number().int().min(-16).max(99),
    category: FloorCategorySchema,
});
// ── GET /devices/{id}/floors ───────────────────────────────────────────────
/** Device-wide (not per-floor) voice-command toggles (BUG-16). */
export const VoiceCommandsSchema = z.object({
    voiceDoorOpen: z.boolean(),
    voiceDoorClose: z.boolean(),
    voiceCallLift: z.boolean(),
});
export const ListFloorsResponse = z.object({
    floors: z.array(FloorSchema),
}).merge(VoiceCommandsSchema);
// ── PUT /devices/{id}/floors ───────────────────────────────────────────────
export const FloorEntrySchema = z.object({
    label: z.string().min(1).max(64),
    /** SIGNED physical ordinal (ground = 0) — see FloorSchema.ordinal. */
    ordinal: z.number().int().min(-16).max(99),
    building: z.string().min(1).max(128).optional(),
    // Defaulted so older app builds that omit it keep working unchanged.
    category: FloorCategorySchema.default('floor'),
});
/**
 * PUT /devices/{id}/floors — saves to the DB AND (on a structural change)
 * pushes the layout to the controller as apply_config in the same
 * transaction. OFFLINE RULE: a structural save against an offline lift is
 * REFUSED with 409 { code: 'DEVICE_OFFLINE' } and nothing is written — the
 * app shows "Setup not saved — device is offline". A name-only save (labels
 * changed, structure identical) skips the device push entirely and works
 * offline; existing floorIds are preserved across saves by matching on
 * (category, ordinal) then (category, label).
 */
export const ReplaceFloorsBody = z.object({
    /** Full desired floor list — server replaces the device's floors atomically. */
    floors: z.array(FloorEntrySchema).min(1),
}).merge(VoiceCommandsSchema.partial());
export const ReplaceFloorsResponse = z.object({
    floors: z.array(FloorSchema),
    /**
     * cmdId of the apply_config sent to the controller — the app correlates the
     * async config_status frame (APPLIED / REJECTED+reason) against this. Null
     * when no push happened (name-only save).
     */
    applyCmdId: z.string().nullable(),
}).merge(VoiceCommandsSchema);
//# sourceMappingURL=floors.js.map